import { CategoryIds } from '../../types/categories';
import {
  MatchStatuses,
  TvChannels,
  type MatchScore,
  type Highlight,
  type MatchStatus,
  type Scorer,
  RugbyScorerTypes,
} from '../../types/match';
import { PeriodId } from '../../types/periodIds';
import { SportIds, type SportId } from '../../types/sports';
import { type TopMyMatch } from '../../types/topMyMatch';
import { type HistoryMatchState } from '../history';
import { type CompetitorType } from '../scoreboards';

import { type BetState } from './bets';
import { type TournamentState } from './tournaments';

class MatchEntityStatus {
  static isPrematch = (status: string | null | undefined): boolean => {
    return String(status).toLowerCase() === MatchStatuses.PREMATCH;
  };

  static isLive = (status: string | null | undefined): boolean => {
    return String(status).toLowerCase() === MatchStatuses.LIVE;
  };

  static isCancelled = (status: string | null | undefined): boolean => {
    return String(status).toLowerCase() === MatchStatuses.CANCELLED;
  };

  static isEnded = (status: string | null | undefined): boolean => {
    return String(status).toLowerCase() === MatchStatuses.ENDED;
  };
}

export { MatchEntityStatus };

class MatchEntityCompetitors {
  /**
   * Retourne true si le match a au moins 1 compétiteur
   */
  static hasAtLeastOneCompetitor = (competitors: [CompetitorType, CompetitorType]): boolean => {
    return competitors !== undefined && competitors?.length >= 1;
  };

  /**
   * Retourne true si le match a au moins 2 compétiteurs
   */
  static hasAtLeastTwoCompetitors = (competitors: [CompetitorType, CompetitorType]): boolean =>
    competitors !== undefined && competitors?.length >= 2;

  /**
   * Retourne true si au moins un des deux competitors contient des scorers
   */
  static hasScorer = (
    competitors: [CompetitorType, CompetitorType],
    hasAtLeastOneCompetitor: boolean,
    hasAtLeastTwoCompetitors: boolean,
  ): boolean => {
    return (
      (competitors !== undefined && ((hasAtLeastOneCompetitor && competitors[0].scorers) || []).length > 0) ||
      ((hasAtLeastTwoCompetitors && competitors[1].scorers) || []).length > 0
    );
  };
}

export { MatchEntityCompetitors };

export type EntitiesMatchesEntityHallOfFameBetslipsState = {
  biggestCombo: Array<number>;
  biggestMymatch: Array<number>;
  biggestWinningsSimple: Array<number>;
  biggestOdds: Array<number>;
};

export type MatchId = number;

export type MatchState = {
  matchId: MatchId;
  mainBetId: number | null;
  moreBets: number;
  available: boolean;
  score: string;
  period: string;
  periodId?: PeriodId;
  periodName?: string;
  hlType: string;
  matchtime: number;
  matchtimeExtended: string;
  mymatchAvailable?: boolean;
  setScores: string;
  status: MatchStatus;
  title: string;
  roundName: string;
  tvChannels: string | null; // A challenger, StringEnum ?
  sportId: SportId;
  categoryId: number;
  tournamentId: number;
  competitor1Id: number;
  competitor1Name: string;
  competitor1Flag?: string;
  competitor2Id: number;
  competitor2Name: string;
  competitor2Flag?: string;
  matchStart: number;
  matchEnd?: number;
  redCardsHome: number;
  redCardsAway: number;
  yellowCardsAway: number;
  yellowCardsHome: number;
  yellowRedCardsAway: number;
  yellowRedCardsHome: number;
  betradarHasStatistics: boolean;
  stream?: string;
  hasTracker: boolean | null;
  skillcornerId: number | null;
  scorers: Array<Scorer>;
  expectedT1: number | null;
  expectedT2: number | null;
  bets: Array<number>;
  remainingTime: string;
  remainingTimeInPeriod: string;
  clockStopped: boolean;
  server: number;
  suspendHome: boolean;
  suspendAway: boolean;
  srSeasonId: string | null;
  srTournamentId: string | null;
  stageId: number | null;
  parentStageId: number | null;
  isOutright?: boolean;
  matchPromo?: string;
  matchWarning?: string;
  '@@invalid'?: boolean;
  filters?: Array<number>;
  multimatch?: Array<number>;
  listBetIds?: Array<number>;
  isCancelled?: boolean; // use for grids, can override match status
  img?: string;
  topMymatchs: Array<TopMyMatch>;
  lineupsAvailable?: boolean;
  streamHighlightsAvailable: boolean;
  streamHighlightsAvailableUntil: number;
  highlights?: Array<Highlight>;
  submatches?: Array<number>;
  stoppageTime?: string | null;
  stoppageTimeAnnounced?: string | null;
  summaryComputed?: boolean;
  fieldImg?: string;
  officialLineups?: boolean;
  hallOfFameBetslips: {
    biggestCombo: Array<number>;
    biggestMymatch: Array<number>;
    biggestWinningsSimple: Array<number>;
    biggestOdds: Array<number>;
  };
  odds1?: number;
  odds2?: number;
  oddsX?: number;
  regularScore1?: number;
  regularScore2?: number;
  isPrivateScore?: boolean;
  imgEventId?: number;
  imgFightId?: number;
  hotPeriod?: number;
  startingOrdersAvailable?: boolean;
  totalRaceLaps?: number;
  currentRaceLap?: number;
  nbSubmatchesEnded?: number;
  nbSubmatchesLive?: number;
  nbSubmatchesPending?: number;
};

export type EntitiesMatches = {
  [key: MatchId]: MatchState;
};

class MatchEntity {
  /**
   * Retourne true si le match est de type football
   */
  static isSoccer = (state: MatchState | null): boolean => state?.sportId === SportIds.SOCCER;

  /**
   * Retourne true si le match est de type rugby or rugby league
   */
  static isRugby = (state: MatchState | null): boolean =>
    state?.sportId === SportIds.RUGBY || state?.sportId === SportIds.RUGBY_LEAGUE;

  /**
   * Retourne le nom du compétiteur en fonction de l'équipe
   */
  static getCompetitorName = (state: MatchState | null, team: number | null): string | null | undefined =>
    team === 1 ? state?.competitor1Name : state?.competitor2Name;

  /**
   * Retourne l'id du compétiteur en fonction de l'équipe
   */
  static getCompetitorId = (state: MatchState | null, team: number | null): number | null | undefined =>
    team === 1 ? state?.competitor1Id : state?.competitor2Id;

  /**
   * Retourne le flag du compétiteur en fonction de l'équipe
   */
  static getCompetitorFlag = (state: MatchState | null, team: number | null): string | null | undefined =>
    team === 1 ? state?.competitor1Flag : state?.competitor2Flag;

  /**
   * Retourne true si le match est un multimatch
   */
  static isMultimatch = (state: MatchState | null | undefined): boolean => !!state?.submatches?.length;

  /**
   * Retourne true si le match est fini
   */
  static isEnded = (state: MatchState | null): boolean =>
    state === null || (state !== undefined && MatchEntityStatus.isEnded(state.status));

  /**
   * Retourne true si le status du match est PREMATCH
   */
  static isPrematch = (state: MatchState | null): boolean =>
    state !== undefined && MatchEntityStatus.isPrematch(state?.status);

  /**
   * Retourne true si le status du match est LIVE
   */
  static isLive = (state: MatchState | HistoryMatchState | null): boolean =>
    state !== undefined && MatchEntityStatus.isLive(state?.status);

  /**
   * Retourne true si le match a le status annulé ou si on a une clé spécifique au grille: isCancelled
   */
  static isGridCancelled = (state: MatchState | null): boolean =>
    state ? MatchEntityStatus.isCancelled(state.status) || !!state.isCancelled : false;

  static hasTracker = (match: MatchState | null, tournament: TournamentState | null): boolean => {
    const trackerSportIdSupported = [
      SportIds.SOCCER,
      SportIds.BASKETBALL,
      SportIds.BASEBALL,
      SportIds.ICEHOCKEY,
      SportIds.TENNIS,
      SportIds.HANDBALL,
      SportIds.RUGBY,
      SportIds.RUGBY_7S,
      SportIds.RUGBY_LEAGUE,
      SportIds.FOOTBALL,
      SportIds.TABLE_TENNIS,
      SportIds.VOLLEYBALL,
      SportIds.BADMINTON,
      SportIds.BEACH_VOLLEY,
      SportIds.SNOOKER,
      SportIds.DARTS,
      SportIds.FORMULA_1,
      SportIds.CRICKET,
      SportIds.FUTSAL,
      SportIds.ESPORT,
    ];

    if (match) {
      if (match.sportId === SportIds.FORMULA_1) {
        // Dans le cas de la F1, si on a un parentStageId, on peut activer le tracker
        if (!match.parentStageId) {
          return false;
        }
      } else if (
        match.sportId === SportIds.ESPORT &&
        ![CategoryIds.ESPORT_SOCCER, CategoryIds.ESPORT_BASKETBALL, CategoryIds.ESPORT_ICE_HOCKEY].find(
          (categoryId) => categoryId === match.categoryId,
        )
      ) {
        // Pour l'esport, on active uniquement les categories eFootball, eBasketball, eIceHockey
        return false;
      } else if (match.isOutright) {
        // Sinon, si c'est un outright, on désactive le tracker
        return false;
      }
    }

    return tournament?.disableTracker
      ? false
      : !!(match && !!trackerSportIdSupported.find((sportId) => sportId === match.sportId));
  };

  static hasPlayerStatsTracker = (match: MatchState, tournament: TournamentState): boolean => {
    const playerStatsSportIdSupported = [SportIds.SOCCER, SportIds.BASKETBALL];

    if (tournament?.disablePlayerStats) {
      return false;
    }
    if (match) {
      return (
        !!playerStatsSportIdSupported.find((sportId) => sportId === match.sportId) &&
        (!!match.srTournamentId || !!match.srSeasonId)
      );
    }
    return false;
  };

  static isMyMatchAvailable = (state: MatchState): boolean => {
    if (state) {
      return state['@@invalid'] !== true && !!state.mymatchAvailable;
    }
    return false;
  };

  static getScores = (match?: MatchState | { score?: string | null }): MatchScore => {
    const splittedScore = match?.score?.split(':');
    const score1 = parseInt(splittedScore?.[0] ?? '', 10);
    const score2 = parseInt(splittedScore?.[1] ?? '', 10);
    const isValid = !Number.isNaN(score1) && !Number.isNaN(score2);

    return isValid ? { score1, score2 } : { score1: null, score2: null };
  };

  static getPsoScore = (scores: Array<string> | null): Array<string> | undefined => {
    if (scores && scores.length === 4 && scores[3] !== '0:0') {
      return scores[3].split(':');
    }
  };

  static hasStream = (match?: MatchState): boolean => {
    if (match) {
      const delay = 120; // seconds
      const streamingStart = (match.matchStart ? new Date((match.matchStart - delay) * 1000) : null)?.getTime();
      const streamingEnd = (match.matchEnd ? new Date((match.matchEnd + delay) * 1000) : null)?.getTime();
      const currDateTime = new Date().getTime();

      // Match live or starting soon / ending soon
      return (
        !!match.stream &&
        (MatchEntity.isLive(match) ||
          (!!streamingStart && currDateTime - streamingStart > 0 && currDateTime - streamingStart < delay * 1000) ||
          (!!streamingEnd && currDateTime - streamingEnd < 0 && currDateTime - streamingEnd > delay * -1000))
      );
    }
    return false;
  };

  static isWinamaxTvChannel = (state: MatchState | null): boolean => state?.tvChannels === TvChannels.WINAMAXTV;

  static isMainBet = (state: MatchState, bet: BetState): boolean =>
    state !== null && bet !== null && state.mainBetId === bet.betId;

  static hasTopMyMatchs = (match?: MatchState): boolean => !!match?.topMymatchs?.filter((k) => k.odds > 1)?.length;

  static hasHighlights = (match?: MatchState): boolean => {
    let hasExpired = false;
    const streamHighlightsAvailableUntil = match?.streamHighlightsAvailableUntil;

    if (typeof streamHighlightsAvailableUntil === 'number') {
      hasExpired = new Date().getTime() > streamHighlightsAvailableUntil;
    }

    return !!match?.streamHighlightsAvailable && !hasExpired;
  };

  static getRedCards = (match?: MatchState) =>
    (match?.redCardsAway ?? 0) +
    (match?.redCardsHome ?? 0) +
    (match?.yellowRedCardsAway ?? 0) +
    (match?.yellowRedCardsHome ?? 0);

  // get number of tries for a rugby match
  static getTries = (match: MatchState): Array<Scorer> =>
    match.scorers?.filter((scorer) => scorer?.type === RugbyScorerTypes.TRY) || [];
}

export { MatchEntity };
